import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useSidebarStore } from '../store/sidebarStore';
import { adminApi } from '../api/admin';
import client from '../api/client';
import {
  LayoutDashboard, Calendar, CheckSquare, Settings, Users,
  ChevronLeft, ChevronRight, Scale,
  Folder, Briefcase, User, Heart, Shield, Globe, Car, Home
, FileText, ClipboardList, BookOpen, ChevronDown } from 'lucide-react';

const ICON_MAP = {
  folder: Folder, briefcase: Briefcase, user: User,
  'heart-pulse': Heart, shield: Shield, globe: Globe,
  car: Car, home: Home, scale: Scale,
};

const adminItems = [
  { to: '/admin', icon: Settings, label: 'Admin Panel' },
];

export default function Sidebar({ open, onToggle }) {
  const { user, tenant } = useAuthStore();
  const { refreshKey } = useSidebarStore();
  const navigate = useNavigate();
  const [recordTypes, setRecordTypes] = useState([]);
  const [diaryOpen, setDiaryOpen] = useState(false);
  const [diaryTypes, setDiaryTypes] = useState([]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const { data } = await adminApi.getRecordTypes();
        setRecordTypes(data.record_types.filter(rt => rt.is_active && rt.show_in_sidebar));
      } catch (err) {
        // silently fail
      }
    };
    if (tenant) fetchTypes();
    client.get("/diary/types").then(r => setDiaryTypes(r.data.diary_types || [])).catch(() => {});
  }, [tenant, refreshKey]);

  return (
    <div className={`${open ? 'w-64' : 'w-16'} bg-white border-r border-gray-200 flex flex-col transition-all duration-200`}>
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-gray-200">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <Scale className="h-8 w-8 text-brand-600 flex-shrink-0" />
          {open && (
            <div className="overflow-hidden">
              <span className="text-lg font-bold text-gray-900">Sixty10</span>
              {tenant && (
                <p className="text-xs text-gray-500 truncate">{tenant.name}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
        <NavLink to="/" end
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`
          }>
          <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
          {open && <span>Dashboard</span>}
        </NavLink>

        {recordTypes.length > 0 && open && (
          <div className="pt-3 pb-1 px-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Records</p>
          </div>
        )}
        {recordTypes.map((rt) => {
          const Icon = ICON_MAP[rt.icon] || Folder;
          return (
            <NavLink key={rt.id} to={`/records/${rt.slug}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }>
              <Icon className="h-5 w-5 flex-shrink-0" style={{ color: rt.color }} />
              {open && <span>{rt.name}</span>}
            </NavLink>
          );
        })}

        {open && (
          <div className="pt-3 pb-1 px-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tools</p>
          </div>
        )}
        <NavLink to="/calendar"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`
          }>
          <Calendar className="h-5 w-5 flex-shrink-0" />
          {open && <span>Calendar</span>}
        </NavLink>
        <NavLink to="/tasks"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`
          }>
          <CheckSquare className="h-5 w-5 flex-shrink-0" />
          {open && <span>Tasks</span>}
        </NavLink>

        {/* Diary Section */}
        <button onClick={() => setDiaryOpen(!diaryOpen)}
          className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 flex-shrink-0" />
            {open && <span>Diary</span>}
          </div>
          {open && <ChevronDown className={`h-4 w-4 transition-transform ${diaryOpen ? 'rotate-180' : ''}`} />}
        </button>
        {diaryOpen && open && (
          <div className="ml-4 space-y-0.5">
            <NavLink to="/activity-log"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`
              }>
              <span className="text-base">📊</span>
              <span>Activity Log</span>
            </NavLink>
            <NavLink to="/notes"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`
              }>
              <span className="text-base">📝</span>
              <span>Notes</span>
            </NavLink>
            <NavLink to="/documents-audit"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`
              }>
              <span className="text-base">📄</span>
              <span>Documents</span>
            </NavLink>
            {diaryTypes.filter(dt => !dt.is_system).map(dt => (
              <NavLink key={dt.id} to={`/diary/${dt.slug}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }>
                <span className="text-base">{dt.icon}</span>
                <span>{dt.name}</span>
              </NavLink>
            ))}
          </div>
        )}
        {user?.is_admin && (
          <>
            {open && (
              <div className="pt-4 pb-2 px-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
              </div>
            )}
            {adminItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {open && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <button
        onClick={onToggle}
        className="h-12 flex items-center justify-center border-t border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
      >
        {open ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
      </button>
    </div>
  );
}
